import React, { useMemo, useState } from "react";
import { Trophy, Users, Warning } from "@phosphor-icons/react";
import { SortableTable } from "./SortableTable";
import { customerColumns } from "./columns";
import { inr, num } from "./iaUtils";

const BOARDS = [
  ["sr_created", "Most SRs created", num], ["sr_completed", "Most SRs completed", num],
  ["inv_generated", "Most invoices", num], ["paid", "Highest paid", inr],
  ["due", "Highest due", inr], ["prepaid", "Most prepaid", inr],
];
const SEGMENTS = [["all", "All"], ["paid_only", "Paid only"], ["has_dues", "Has dues"], ["prepaid", "Prepaid customers"], ["inactive", "No activity"]];

function Leaderboard({ k, title, fmt, rows, onPick }) {
  return (
    <div className="bg-white border border-[#E5E2D9] rounded-md p-3" data-testid={`ia-lb-${k}`}>
      <div className="flex items-center gap-1.5 text-xs font-semibold"><Trophy size={13} className="text-[#D4A017]"/>{title}</div>
      <ol className="mt-2 space-y-1">
        {rows.length === 0 && <li className="text-[11px] text-[#8A8780]">No data</li>}
        {rows.map((r, i) => (
          <li key={r.customer_id}>
            <button onClick={() => onPick(r)} data-testid={`ia-lb-${k}-${i}`}
                    className="w-full flex items-center gap-2 text-[12px] hover:bg-[#FBF6EF] rounded px-1 py-0.5 text-left">
              <span className="w-4 text-[10px] font-mono text-[#8A8780]">{i + 1}</span>
              <span className="flex-1 truncate">{r.name}</span>
              <span className="font-mono">{fmt(r[k])}</span>
            </button>
          </li>
        ))}
      </ol>
    </div>
  );
}

export function CustomersTab({ data, onPick }) {
  const [seg, setSeg] = useState("all");
  const [q, setQ] = useState("");
  const rows = useMemo(() => {
    const base = seg === "inactive" ? data.inactive.map(c => ({ ...c, sr_created: 0, sr_completed: 0, inv_generated: 0,
      inv_paid: 0, inv_unpaid: 0, billed: 0, paid: 0, due: 0, prepaid: 0 })) : data.customers.filter(r =>
      seg === "all" || (seg === "paid_only" && r.billed > 0 && r.due === 0) || (seg === "has_dues" && r.due > 0)
      || (seg === "prepaid" && r.prepaid > 0));
    const ql = q.toLowerCase();
    return ql ? base.filter(r => [r.name, r.phone, r.code].some(v => (v || "").toLowerCase().includes(ql))) : base;
  }, [data, seg, q]);

  return (
    <div className="space-y-4" data-testid="ia-customers-tab">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="bg-white border border-[#E5E2D9] rounded-md p-3" data-testid="ia-split">
          <div className="flex items-center gap-1.5 text-xs font-semibold"><Users size={13}/>Paid vs unpaid</div>
          <div className="grid grid-cols-3 gap-2 mt-2 text-center">
            <div><div className="font-heading text-lg text-[#2D5A27]" data-testid="ia-split-paid">{num(data.split.fully_paid)}</div><div className="text-[10px] text-[#8A8780]">fully paid</div></div>
            <div><div className="font-heading text-lg text-[#B42828]" data-testid="ia-split-dues">{num(data.split.with_dues)}</div><div className="text-[10px] text-[#8A8780]">with dues</div></div>
            <div><div className="font-heading text-lg" data-testid="ia-split-total">{inr(data.split.total_due)}</div><div className="text-[10px] text-[#8A8780]">total due</div></div>
          </div>
        </div>
        <div className="md:col-span-2 bg-white border border-[#E5E2D9] rounded-md p-3" data-testid="ia-ageing">
          <div className="flex items-center gap-1.5 text-xs font-semibold"><Warning size={13} className="text-[#B42828]"/>Dues ageing (oldest unpaid)</div>
          <div className="grid grid-cols-4 gap-2 mt-2">
            {data.ageing.map((a, i) => (
              <div key={a.bucket} className="rounded p-2 text-center" style={{ background: ["#FEF3C7", "#FED7AA", "#FECACA", "#FCA5A5"][i] }}
                   data-testid={`ia-ageing-${a.bucket}`}>
                <div className="text-[10px] font-semibold">{a.bucket} days</div>
                <div className="font-heading text-base">{inr(a.amount)}</div>
                <div className="text-[10px]">{a.customers} customer{a.customers === 1 ? "" : "s"}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
        {BOARDS.map(([k, t, fmt]) => <Leaderboard key={k} k={k} title={t} fmt={fmt} rows={data.leaderboards[k] || []} onPick={onPick}/>)}
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        {SEGMENTS.map(([k, l]) => (
          <button key={k} onClick={() => setSeg(k)} data-testid={`ia-seg-${k}`}
                  className={`h-8 px-3 rounded-full text-xs font-medium ${seg === k ? "bg-[#2D5A27] text-white" : "bg-white border border-[#E5E2D9] text-[#5C5A55]"}`}>{l}</button>
        ))}
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search customers…" data-testid="ia-cust-search"
               className="ml-auto h-8 px-3 border border-[#E5E2D9] rounded-md text-xs w-56"/>
        <span className="text-[11px] text-[#8A8780]" data-testid="ia-cust-count">{rows.length} customers</span>
      </div>
      <SortableTable testid="ia-cust-table" columns={customerColumns} rows={rows} rowKey={r => r.customer_id}
                     onRowClick={onPick} initialSort={{ key: "billed", dir: "desc" }}/>
    </div>
  );
}
